import AgendamentoInterceptor from '@finx/web-repository/src/apis/Agendamento/AgendamentoInterceptor'
import SegurancaApiInterceptor from '@finx/web-repository/src/apis/Seguranca/SegurancaApiInterceptor'
import FactoryResponse from '@/factories/FactoryResponse'
// eslint-disable-next-line import/no-cycle
import { backofficeRoute, formatDateToISO, normalizeOrigemDaSolicitacao, hasValue } from '@/utils/common'
import { Anestesista, BodyRequest, SolicitacaoAcoesBackofficeEnum, CotacaoOperadorasExtraGrupo,
  TipoOrigemEnum, EmailMensagemQuery, SendReservaSala, HospitalConfiguracaoOperadora,
  TipoDocumentoUpload, ResolvePacienteDuplicadoPayload, FactoryResponse as ResponseApi } from '@/types/_'
import { IAgrupadorHospital } from '@/types/interfaces/agrupador-hospital'

const factory = new FactoryResponse()
const api = new AgendamentoInterceptor()
const segurancaApi = new SegurancaApiInterceptor()

export interface RegraReservaSalaUpsertResponse {
  id: string,
  avisos: string[],
}

export default {
  async cadastrarMedicoBackoffice(params: any) {
    const request = await api.post('backoffice/cadastrar-medico-backoffice', params)
    return factory.build(request)
  },

  async backofficeAssociaPreCadastroExistente(params: any) {
    const request = await api.put('backoffice/confirma-pre-cadastro-medico-existente', params)
    return factory.build(request)
  },

  async getAllSearch(param: any) {
    // Mapeamento de propriedades que precisam ser renomeadas
    const propriedadesParaParse: Record<string, string> = {
      paciente: 'pacienteNome',
      tipoPeriodo: 'tipoDeFiltroDePeriodo',
      hospitalId: 'hospitalid',
    }

    // Propriedades que precisam de tratamento especial (não incluídas no mapeamento automático)
    const propriedadesEspeciais = [
      'etapa',
      'de',
      'ate',
      'preanestesia',
      'statusAvaliacaoPreAnestesiaQuery',
      'situacao',
      'origemDaSolicitacao',
      'opme',
      'todosOsStatus',
      'pagina',
      'qtdPorPagina',
      'orderBy',
    ]

    const propriedadesNome = Object.keys(propriedadesParaParse)
    const propriedadesNomeParam = Object.keys(param).filter(
      propriedade => !propriedadesNome.includes(propriedade) && !propriedadesEspeciais.includes(propriedade),
    )

    // Construir o body do POST
    const body: any = {
      // Campos obrigatórios
      area: param.etapa || 'Agendamento',
      orderBy: param.orderBy || 'DataUltimaInteracaoAsc',
      pagina: param.pagina || 1,
      qtdPorPagina: param.qtdPorPagina || 25,
    }

    // Processar propriedades que precisam ser renomeadas
    propriedadesNome.forEach((propriedade) => {
      if (!hasValue(param[propriedade])) return

      const propriedadeKey = propriedadesParaParse[propriedade]
      body[propriedadeKey] = param[propriedade]
    })

    // Processar propriedades que não precisam de mapeamento
    propriedadesNomeParam.forEach((propriedade) => {
      if (!hasValue(param[propriedade])) return

      // Validações específicas para arrays
      if (Array.isArray(param[propriedade]) && param[propriedade].length === 0) return

      // Validações específicas para strings vazias
      if (typeof param[propriedade] === 'string' && param[propriedade] === '') return

      body[propriedade] = param[propriedade]
    })

    // Tratamentos especiais

    // Datas formatadas
    const dataInicio = formatDateToISO(param.de)
    if (dataInicio) {
      body.dataInicio = dataInicio
    }

    const dataFim = formatDateToISO(param.ate)
    if (dataFim) {
      body.dataFim = dataFim
    }

    // StatusAvaliacaoPreAnestesia - pode vir de statusAvaliacaoPreAnestesiaQuery ou preanestesia
    if (param.todosOsStatus !== true) {
      if (hasValue(param.statusAvaliacaoPreAnestesiaQuery) && Array.isArray(param.statusAvaliacaoPreAnestesiaQuery)) {
        body.statusAvaliacaoPreAnestesia = param.statusAvaliacaoPreAnestesiaQuery
      } else if (hasValue(param.preanestesia)) {
        body.statusAvaliacaoPreAnestesia = [param.preanestesia]
      }
    }

    // Situação - lógica especial para Farmacia
    if (hasValue(param.situacao) && Array.isArray(param.situacao)) {
      if (param.todosOsStatus !== true) {
        if (param.etapa && String(param.etapa).toLowerCase() === 'farmacia') {
          body.statusSituacaoFarmacia = param.situacao
        } else {
          body.situacao = param.situacao
        }
      }
    }

    // origemDaSolicitacao deve ser um array de strings
    const origemNormalizada = normalizeOrigemDaSolicitacao(param.origemDaSolicitacao)
    if (origemNormalizada) {
      body.origemDaSolicitacao = origemNormalizada
    }

    // opme - converter string "true"/"false" para boolean
    if (hasValue(param.opme)) {
      body.opme = param.opme === 'true' || param.opme === true
    }

    // todosOsStatus
    if (param.todosOsStatus !== undefined && param.todosOsStatus !== null) {
      body.todosOsStatus = param.todosOsStatus
    }

    // Tratamento especial para dataAgendamentoVirtual: se não estiver marcado, enviar null
    // Backend atualmente quebra se enviar false que é o valor padrão para quando não está marcado o checkbox
    if (param.dataAgendamentoVirtual === true) {
      // Se estiver marcado, já será processado pelo loop acima
    } else {
      // Se não estiver marcado (false, null, undefined), enviar null
      body.dataAgendamentoVirtual = null
    }

    const request = await api.post('solicitacoes-backoffice', body)

    return factory.build(request)
  },

  async getAllMedicoByUsuarioId(usuarioId: string) {
    const request = await api.get(`usuario-hospital/${usuarioId}/medicos`)
    return factory.build(request)
  },

  async getAllConvenios() {
    const request = await api.get('/usuario-hospital/convenios')
    return factory.build(request)
  },

  async cancelarSolicitacao(solicitacaoId: string, tipoOrigem: TipoOrigemEnum, params: any) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/${tipoOrigem}/cancelar`, params)
    return factory.build(request)
  },

  async solicitaCancelamentoSolicitacao(solicitacaoId: string, motivo: string) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/solicita-cancelamento?motivoCancelamento=${motivo}`, '')
    return factory.build(request)
  },

  async cancelarSolicitacaoMedico(solicitacaoId: string, motivo: string, observacaoDoCancelamento: string) {
    const request = await api.put(`solicitacoes/${solicitacaoId}/medico/solicita-cancelamento?motivoCancelamento=${motivo}`, {
      observacaoDoCancelamento,
    })
    return factory.build(request)
  },

  async deactivateDoctorSurgerySolicitation(surgerySolicitationId, body) {
    const APIRequest = await api.put(`solicitacoes/${surgerySolicitationId}/medico/MedicoCancelaSolicitacao?`, body)

    return factory.build(APIRequest)
  },

  async getMotivosCancelamentoAll(params: any) {
    const query = params.etapa ? `?etapa=${params.etapa}` : ''
    const request = await api.get(`hospitais/${params.hospitalId}/motivos-cancelamento${query}`)
    return factory.build(request)
  },

  // TODO: Remover esse endpoint...

  async getEtapaSolicitacaoById(solicitacaoId: string) { // Requisição desnecessária
    const request = await api.get(`solicitacoes-backoffice/${solicitacaoId}?area=${backofficeRoute()}&acao=visualizar&consultaMedico=true`)
    return factory.build(request)
  },

  async getSolicitacaoByEtapa(solicitacao: any) {
    const request = await api.get(`solicitacoes-backoffice/${solicitacao.solicitacaoId}?area=${solicitacao.area || backofficeRoute()}&acao=visualizar&consultaMedico=true`)
    return factory.build(request)
  },

  async getReservaSalas(params: any) {
    let query: string = ''
    const keys: string[] = ['data', 'solicitacaoId', 'hora', 'tempoCirurgia', 'hospitalId']
    keys.map((k) => {
      if (params[k]) {
        query += `&${k}=${params[k]}`
      }
      return k
    })

    query = query.substr(1, query.length - 1)

    const request = await api.get(`salas/reservas?${query}`)
    return factory.build(request)
  },

  async getDoctorRoomReservations(params: any) {
    let query: string = ''
    const keys: string[] = ['data', 'solicitacaoId', 'hora', 'tempoCirurgia']
    keys.map((k) => {
      if (params[k]) {
        query += `&${k}=${params[k]}`
      }
      return k
    })

    query = query.substr(1, query.length - 1)

    const request = await api.get(`medico/reserva/sala?${query}`)
    return factory.build(request)
  },

  async getRoomConfigurationBySurgeryCenterAndHospital(params: any) {
    let query: string = ''
    const keys: string[] = ['centroCirurgicoId', 'hospitalId']
    keys.map((k) => {
      if (params[k]) {
        query += `&${k}=${params[k]}`
      }
      return k
    })

    query = query.substr(1, query.length - 1)

    const request = await api.get(`configuracao-sala-centro-cirurgico?${query}`)
    return factory.build(request)
  },

  async getRoomAvailabilityCalendar(queryParamsObject) {
    const queryParams = new URLSearchParams(queryParamsObject).toString()

    const APIRequest = await api.get(`calendario-indisponibilidade-sala?${queryParams}`)
    return factory.build(APIRequest)
  },

  async updateRoomAvailabilityCalendar(requestBody: any) {
    const APIRequest = await api.post('calendario-indisponibilidade-sala', requestBody)
    return factory.build(APIRequest)
  },

  async updateRoomConfiguration(requestBody: any) {
    const APIRequest = await api.post('configuracao-sala-centro-cirurgico', requestBody)
    return factory.build(APIRequest)
  },

  async getRoomStatusBySurgeryCenterAndHospital(params: any) {
    let query: string = ''
    const keys: string[] = ['salaCentroCirurgicoId', 'hospitalId']
    keys.map((k) => {
      if (params[k]) {
        query += `&${k}=${params[k]}`
      }
      return k
    })

    query = query.substr(1, query.length - 1)

    const request = await api.get(`status-salas-centro-cirurgico?${query}`)
    return factory.build(request)
  },


  async updateRoomStatusSurgeryCenter(params: any) {
    const request = await api.put('solicitacoes/atualiza-status-sala-centro-cirurgico', params)
    return factory.build(request)
  },

  async updateReservationLimitDate(params: any) {
    const request = await api.put('hospital/atualiza-maximo-dias-medico-sem-reserva', params)
    return factory.build(request)
  },

  async deleteRoomAvailability(queryParams) {
    const { disponibilidadeSalaId, hospitalId } = queryParams

    const request = await api.delete(`hospitais/${hospitalId}/disponibilidade-sala/${disponibilidadeSalaId}`)

    return factory.build(request)
  },

  async getRoomShiftConfigurationBySurgeryCenterAndHospital(params: any) {
    let query: string = ''
    const keys: string[] = ['centroCirurgicoId', 'hospitalId']
    keys.map((k) => {
      if (params[k]) {
        query += `&${k}=${params[k]}`
      }
      return k
    })

    query = query.substr(1, query.length - 1)

    const request = await api.get(`configuracao-turno-centro-cirurgico?${query}`)
    return factory.build(request)
  },

  async updateShiftConfiguration(params: any) {
    const request = await api.post('configuracao-turno-centro-cirurgico', params)
    return factory.build(request)
  },

  async sendReservaSalas(params: SendReservaSala) {
    const request = await api.post('salas/reservas', params)
    return factory.build(request)
  },

  async backofficeAgendamentoAlteraReserva(solicitacaoId: string, params: any) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/reservas`, params)
    return factory.build(request)
  },

  async getAutorizarProcessoBySolicitacaoId(solicitacaoId: string) {
    const request = await api.get(`/${solicitacaoId}`)
    return factory.build(request)
  },

  async getReservaDeSangue(solicitacaoId: string) {
    const request = await api.get(`/solicitacoes/${solicitacaoId}/reserva-sangue`)
    return factory.build(request)
  },

  async putReservaDeSangue(params: any) {
    const request = await api.put(`/solicitacoes/${params.solicitacaoId}/reserva-sangue`, params)
    return factory.build(request)
  },

  async backofficeAgendamentoAtualizaSolicitacao(params: any) {
    const request = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}`, params)
    return factory.build(request)
  },

  async backofficeAgendamentoResolvePendenciaDePacienteNoHis(params: any) {
    const request = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/resolvePendenciaDePacienteNoHis`, params)
    return factory.build(request)
  },

  async resolvePacienteDuplicado(params: ResolvePacienteDuplicadoPayload): Promise<ResponseApi<void>> {
    const request = await api.put('solicitacao/paciente/resolvePacienteDuplicado', params)
    return factory.build<void>(request)
  },

  async autorizaSolicitacao(solicitacaoId: string, acao: SolicitacaoAcoesBackofficeEnum, bodyRequest: BodyRequest) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/autorizacao/${acao}`, bodyRequest)
    return factory.build(request)
  },

  async renovarAutorizacaoOperadora(solicitacaoId: string) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/autorizacao/autorizacao-renovacao`, '')
    return factory.build(request)
  },

  async suprimentosFazCotacao(solicitacaoId: string, acao: SolicitacaoAcoesBackofficeEnum, bodyRequest: BodyRequest) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/suprimentos/${acao}`, bodyRequest)
    return factory.build(request)
  },

  async anestesistaFazAvaliacao(anestesista: Anestesista) {
    const { solicitacaoId } = anestesista
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/anestesista/avalia`, anestesista)
    return factory.build(request)
  },

  async anestesistaSolicitaCancelamento(solicitacaoId: string) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/solicita-cancelamento`, 'AvaliacaoPreAnestesiaPacienteNaoApto')
    return factory.build(request)
  },

  async medicoAssistentesAll() {
    const request = await api.get('medico/medico-assistentes')
    return factory.build(request)
  },

  async associarConta(params: any) {
    const request = await api.postAllowAnonymous(`agendamento/medicos/convite/${params.conviteId}/assistentes/${params.cpf}/local-atendimento/${params.localAtendimentoNome}`, params)
    return factory.build(request)
  },

  async associarContaSecretaria(params: any) {
    const request = await api.post(`agendamento/medicos/${params.medicoId}/assistentes/${params.cpf}/local-atendimento/${params.localAtendimentoId}`, params)
    return factory.build(request)
  },

  async putNovaDataSolicitacao(solicitacaoId: string, novaDataSolicitada: any) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/DataAgendamento`, novaDataSolicitada)
    return factory.build(request)
  },

  async enviarNotificacaoWhatsapp(solicitacaoId: string, params: any) {
    const request = await api.post(`solicitacoes/${solicitacaoId}/notificacao-whatsapp/enviar`, params)
    return factory.build(request)
  },

  async solicitacaoAuditada(params: any) {
    const request = await api.put(`backoffice/auditoria/${params.solicitacaoId}/solicitacao-auditada`, params)
    return factory.build(request)
  },

  async putAutoriza(solicitacaoId: string) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/Autoriza`, '')
    return factory.build(request)
  },

  async getCheckNumeroReserva(solicitacaoId: string, hospitalId: string, numeroReserva: string) {
    const request = await api.get(`solicitacoes/numero-reserva/${solicitacaoId}/${hospitalId}/${numeroReserva}/exists`)
    return factory.build(request)
  },

  async backofficeAgendamentoRealizaTriagem(solicitacaoId: string, params: any) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/agendamento/realiza-triagem`, params)
    return factory.build(request)
  },

  async atualizarTriagem(solicitacaoId: string) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/agendamento/atualiza-triagem`, '')
    return factory.build(request)
  },

  async autorizacaoAlteraCotacaoExtraGrupo(solicitacaoId: string, data: { cotacaoOperadorasExtraGrupo: CotacaoOperadorasExtraGrupo | null }) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/autorizacao/altera-cotacao`, data)
    return factory.build(request)
  },

  async bloquearAgendamento(solicitacaoId: string) {
    const request = await api.post(`solicitacoes/${solicitacaoId}/bloquear`, '')
    return factory.build(request)
  },

  async desbloquearAgendamento(solicitacaoId: string) {
    const request = await api.post(`solicitacoes/${solicitacaoId}/desbloquear`, '')
    return factory.build(request)
  },

  async getAllConveniosByUsuarioHospitalLogado() {
    const request = await api.get('/usuario-hospital/convenios')
    return factory.build(request)
  },

  async getHospitalCurrentUser() {
    const request = await api.get('usuario-hospital/current/hospitais')
    return factory.build(request)
  },

  async getHospitalById(id: string) {
    const request = await api.get(`/hospitais/${id}`)
    return factory.build(request)
  },

  async getEmailsRecebidos(param: EmailMensagemQuery) {
    const { area, pagina, qtdPorPagina } = param
    const query: string = `area=${area}&pagina=${pagina || 1}&qtdPorPagina=${qtdPorPagina || 25}`
    const request = await api.get(`agendamento/email-recebido?${query}`)
    return factory.build(request)
  },

  async backofficeResolveEmail(emailRecebidoId: string) {
    const request = await api.put(`agendamento/email-recebido/${emailRecebidoId}/resolve-mensagem`, '')
    return factory.build(request)
  },

  async checkSecretariaCpf(cpf: string) {
    const response = await api.getAllowAnonymous(`secretaria/${cpf}`)
    return factory.build(response)
  },

  async getSolicitacoesMapaCirurgico(param: any) {
    // Mapeamento de propriedades que precisam ser renomeadas
    const propriedadesParaParse: Record<string, string> = {
      paciente: 'pacienteNome',
      centroCirurgico: 'centroCirurgicoId',
    }

    // Propriedades que precisam de tratamento especial (não incluídas no mapeamento automático)
    const propriedadesEspeciais = [
      'medicoId',
      'hospitalId',
      'de',
      'ate',
      'opme',
      'pagina',
      'qtdPorPagina',
    ]

    const propriedadesNome = Object.keys(propriedadesParaParse)
    const propriedadesNomeParam = Object.keys(param).filter(
      propriedade => !propriedadesNome.includes(propriedade) && !propriedadesEspeciais.includes(propriedade),
    )

    // Construir o body do POST
    const body: any = {}

    // Processar propriedades que precisam ser renomeadas
    propriedadesNome.forEach((propriedade) => {
      if (!hasValue(param[propriedade])) return

      const propriedadeKey = propriedadesParaParse[propriedade]
      body[propriedadeKey] = param[propriedade]
    })

    // Processar propriedades que não precisam de mapeamento
    propriedadesNomeParam.forEach((propriedade) => {
      if (!hasValue(param[propriedade])) return

      // Validações específicas para arrays
      if (Array.isArray(param[propriedade]) && param[propriedade].length === 0) return

      // Validações específicas para strings vazias
      if (typeof param[propriedade] === 'string' && param[propriedade] === '') return

      body[propriedade] = param[propriedade]
    })

    // Tratamentos especiais

    // Datas formatadas
    const dataInicio = formatDateToISO(param.de)
    if (dataInicio) {
      body.dataInicio = dataInicio
    }

    const dataFim = formatDateToISO(param.ate)
    if (dataFim) {
      body.dataFim = dataFim
    }

    // medicoId → medicosId (array)
    if (hasValue(param.medicoId)) {
      body.medicosId = Array.isArray(param.medicoId) ? param.medicoId : [param.medicoId]
    }

    // hospitalId → hospitalIds (array)
    if (hasValue(param.hospitalId)) {
      body.hospitalIds = Array.isArray(param.hospitalId) ? param.hospitalId : [param.hospitalId]
    }

    // opme - converter string "true"/"false" para boolean
    if (hasValue(param.opme)) {
      body.opme = param.opme === 'true' || param.opme === true
    }

    // Paginação
    body.pagina = param.pagina || 1
    body.qtdPorPagina = param.qtdPorPagina || 25

    const response = await api.post('mapa-cirurgico/listar', body)
    return factory.build(response)
  },

  async criarMapaCirurgico(solicitacoes: any) {
    const response = await api.post('mapa-cirurgico', solicitacoes)
    return factory.build(response)
  },

  async areaMapaCirurgicoConfirmaSolicitacao({ solicitacaoId, areaMapaCirurgico, payload }: any) {
    const response = await api.put(`mapa-cirurgico/solicitacoes/${solicitacaoId}/area/${areaMapaCirurgico}/confirma`, payload)
    return factory.build(response)
  },

  async excluirSolicitacaoMapaCirurgico(params: any) {
    const response = await api.put(`mapa-cirurgico/solicitacoes/${params.solicitacaoId}`, { mensagemDaPendencia: params.mensagemDaPendencia })
    return factory.build(response)
  },

  async getConfiguracoes() {
    const response = await api.get('agendamento/usuario/configuracao')
    return factory.build(response)
  },

  async adicionarEmail(params: any) {
    const response = await api.put(`agendamento/medico/${params.medicoId}/local-atendimento/${params.localAtendimentoId}/email-notificacao`, params)
    return factory.build(response)
  },

  async enviarConviteSecretaria(params: any) {
    const response = await api.put(`${params.medicoId}`, params)
    return factory.build(response)
  },

  async deletarSecretaria(params: any) {
    const response = await api.delete(`agendamento/medico/${params.medicoId}/secretaria/${params.secretariaId}/local-atendimento/${params.localAtendimentoId}`)
    return factory.build(response)
  },

  async deletarEmail(params: any) {
    const response = await api.delete(`agendamento/medico/${params.medicoId}/local-atendimento/${params.localAtendimentoId}/email-notificacao/${params.email}`)
    return factory.build(response)
  },

  async atualizaSecretariaVisualizaSolicitacoesAmbulatorio(params: any) {
    const response = await api.put(`agendamento/secretaria/${params.secretariaId}/visualiza-solicitacoes-ambulatorio`, params)
    return factory.build(response)
  },

  async backofficeVoltaStatus(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/volta-status`, params)
    return factory.build(response)
  },

  async getAssinaturaDoMedico() {
    const response = await api.get('medicos/assinatura')
    return factory.build(response)
  },

  async getSecretariaInfo() {
    const response = await api.get('secretaria/info')
    return factory.build(response)
  },

  async updateAssinaturaDoMedico(params: any) {
    const response = await api.put('medicos/assinatura', params)
    return factory.build(response)
  },

  async backOfficeAgenciaTransfusionalContataPaciente(params: any) {
    const request = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/agencia-transfusional/agenciaTransfusionalContataPaciente`, params)
    return factory.build(request)
  },

  async backOfficeAgenciaTransfusionalRealizaColeta(params: any) {
    const request = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/agencia-transfusional/agenciaTransfusionalRealizacoleta`, params)
    return factory.build(request)
  },

  async backOfficeAgenciaTransfusionalAnalisaColeta(params: any) {
    const request = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/agencia-transfusional/agenciaTransfusionalAnalisaColeta`, params)
    return factory.build(request)
  },

  async backOfficeAgenciaTransfusionalRealizaTeste(params: any) {
    const request = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/agencia-transfusional/agenciaTransfusionalRealizaTeste`, params)
    return factory.build(request)
  },

  async backofficeSuprimentosAvaliaMaterial(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/suprimentos/suprimentosAvaliaMaterial`, params)
    return factory.build(response)
  },

  async backofficeAgendamentoRealizaPreAgendamento(solicitacaoId, data: any) {
    const response = await api.put(`solicitacoes-backoffice/${solicitacaoId}/agendamento/realiza-pre-agendamento`, data)
    return factory.build(response)
  },

  async backofficeAgendamentoRealizaRevisaoCirurgica({ solicitacaoId, solicitacao }) {
    const response = await api.put(`solicitacoes-backoffice/${solicitacaoId}/agendamento/realiza-revisao-cirurgica`, solicitacao)
    return factory.build(response)
  },

  async backofficeAgendamentoFazCancelamento({ solicitacaoId, motivoCancelamento, observacaoDoCancelamento }) {
    const response = await api.put(`solicitacoes-backoffice/${solicitacaoId}/agendamento/cancelar`, motivoCancelamento)
    return response
  },

  async associarMedicoHospital(params: any) {
    const response = await api.post(`agendamento/medico/${params.medicoId}/associar-medico-hospital`, {})
    return factory.build(response)
  },

  async getSecretariaByCpf(params: any) {
    const response = await api.get(`secretaria/${params.cpf}`)
    return factory.build(response)
  },

  async getUsuarioTipoExistsByCpf(params: any) {
    const request = await api.getAllowAnonymous(`/medico/check-exists-by-cpf/${params.cpf}`)
    return factory.build(request)
  },

  async getTermosAceites(params: any) {
    let query: string = ''
    params.hospitais.map((c) => {
      if (c) {
        query += `&hospitais=${c}`
      }
      return c
    })

    query = query.substring(1, query.length)
    const request = await api.getAllowAnonymous(`/hospitais/termo?${query}`)
    return factory.build(request)
  },

  async getSolicitacaoGuias(params: any) {
    const request = await api.post(`solicitacoes-backoffice/${params.solicitacaoId}/autorizacao/guia-tiss`, params)
    return factory.build(request)
  },

  async backofficeSuprimentosConfirmaReservaDeMateriais(solicitacaoId: string) {
    const request = await api.put(`solicitacoes-backoffice/${solicitacaoId}/suprimentos/suprimentosConfirmaReservaDeMateriais`, '')
    return factory.build(request)
  },

  async getProcedimentosInternacaoBackofficeSearch(hospitalId: string, searchText: string) {
    const request = await api.get(`hospitais/${hospitalId}/procedimentos?SearchText=${searchText}`)
    return factory.build(request)
  },

  async getProcedimentoBackofficeSearch(hospitalId: string, searchText: string, convenioId: string) {
    const request = await api.get(`hospitais/${hospitalId}/procedimentos?ConvenioId=${convenioId}&SearchText=${searchText}`)
    return factory.build(request)
  },

  async getProcedimentoPagedSearch(params: any) {
    const { searchText, page, total, tipoProcedimento } = params

    let query = ''

    if (searchText) {
      query += `&searchtext=${searchText}`
    }

    if (page) {
      query += `&page=${page}`
    }

    if (total) {
      query += `&total=${total}`
    }

    if (tipoProcedimento) {
      query += `&tipoProcedimento=${tipoProcedimento}`
    }

    const request = await api.get(`hospitais/${params.hospitalId}/procedimentos/search?${query.substring(1, query.length)}`)
    return factory.build(request)
  },

  async upsertProcedimentoHospital(params: any) {
    const request = await api.post(`hospitais/${params.hospitalId}/procedimentos`, params)
    return factory.build(request)
  },

  async upsertConvenioHospital(params: any) {
    const request = await api.post(`hospitais/${params.hospitalId}/convenio`, params)
    return factory.build(request)
  },

  async upsertConvenioPlanoHospital(params: any) {
    const request = await api.post(`hospitais/${params.hospitalId}/convenios/${params.convenioId}/plano`, params)
    return factory.build(request)
  },

  async postProcedimentoBackoffice(solicitacaoId: string, procedimento: any) {
    const request = await api.post(`solicitacoes/${solicitacaoId}/procedimentos`, procedimento)
    return factory.build(request)
  },

  async deleteProcedimentoBackoffice(solicitacaoId: string, procedimentoId: string) {
    const request = await api.delete(`procedimento/${solicitacaoId}/${procedimentoId}/solicitacao-procedimento`)
    return factory.build(request)
  },

  async upsertHospital(params: any) {
    const response = await api.put('hospital', params)
    return factory.build(response)
  },

  async usuarioAgendamentoUpsert(params: any) {
    const response = await api.put('suporte/perfil-agendamento', params)
    return factory.build(response)
  },

  async getPermissoes() {
    const result = await segurancaApi.get('permissoes')
    return factory.build(result)
  },

  async getUsuarioAgendamentoById(id: string) {
    const response = await api.get(`suporte/usuarios/${id}/perfil-agendamento`)
    return factory.build(response)
  },

  async getTipoUsuario(usuarioId: string) {
    const response = await api.get(`usuario/${usuarioId}/tipo`)
    return factory.build(response)
  },

  async getHospitalCompletoById(id: string) {
    const response = await api.get(`suporte/hospital/${id}`)
    return factory.build(response)
  },

  async cadastrarConvenioPlanos(params: any) {
    const response = await api.post('convenio-plano/merge', params)
    return factory.build(response)
  },

  async cadastrarConvenioProcedimentos(params: any) {
    const response = await api.post('convenio-procedimento/merge', params)
    return factory.build(response)
  },

  async cadastrarConvenioFornecedores(params: any) {
    const response = await api.post('convenio-fornecedor/merge', params)
    return factory.build(response)
  },

  async cadastrarMaterialOpmes(params: any) {
    const response = await api.post('material-opme/merge', params)
    return factory.build(response)
  },

  async cadastrarEquipamentos(params: any) {
    const response = await api.post('equipamento/merge', params)
    return factory.build(response)
  },

  async deleteSurgerySolicitationEquipmentById(surgerySolicitationId: string, equipmentId: string) {
    const response = await api.delete(`solicitacoes/${surgerySolicitationId}/equipamentos/${equipmentId}`)
    return factory.build(response)
  },

  async backofficeSuprimentosAtualizaPrecificacaoOperadora(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/suprimentos/suprimentosAtualizaPrecificacaoOperadora`, params)
    return factory.build(response)
  },

  async updateGrupoOpmeDaSolicitacao(params: any) {
    const response = await api.put(`solicitacoes/${params.solicitacaoId}/grupos-opme`, params)
    return factory.build(response)
  },

  async backofficeAgendamentoDesfazCancelamento(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/agendamento/desfaz-cancelamento`, params)
    return factory.build(response)
  },

  async getPrecacucoesPacienteFromTasy(solicitacaoId: string) {
    const response = await api.get(`solicitacoes-backoffice/${solicitacaoId}/paciente/precaucoes`)
    return factory.build(response)
  },

  async verificaSeExistePreCadastroMedicoHospital(cpf: string) {
    const response = await api.get(`medicos/${cpf}/verifica-medico-cadastrado-hospitais-usuario`)
    return factory.build(response)
  },

  async getHospitalConfiguracaoOperadora(hospitalId: string) {
    const response = await api.get(`suporte/hospital/${hospitalId}/configuracao-operadora`)
    return factory.build(response)
  },

  async enviarHospitalConfiguracaoOperadora(hospitalId: string, configuracao: HospitalConfiguracaoOperadora) {
    const response = await api.put(`suporte/hospital/${hospitalId}/configuracao-operadora`, configuracao)
    return factory.build(response)
  },

  async getConfiguracaoOperadoraById(hospitalId: string, convenioId: string) {
    const response = await api.get(`suporte/hospital/${hospitalId}/convenio/${convenioId}/configuracao-operadora`)
    return factory.build(response)
  },

  async enviarOperadora(hospitalId: string, convenioId: string, params: any) {
    const response = await api.put(`suporte/hospital/${hospitalId}/convenio/${convenioId}/configuracao-operadora`, params)
    return factory.build(response)
  },

  async consultaValorNegociadoNoHis(params: any) {
    const response = await api.put(`solicitacao/${params.solicitacaoId}/opme/consultaValorNegociadoNoHis`, params)
    return factory.build(response)
  },

  async getMotivosTransferenciaReservaByHospital(params: any) {
    const response = await api.get(`hospitais/${params.hospitalId}/motivos-transferencia-reserva`)
    return factory.build(response)
  },

  async cadastrarMotivoTransferenciaReserva(params: any) {
    const response = await api.put('hospitais/motivo-transferencia-reserva', params)
    return factory.build(response)
  },

  async atualizarMotivoTransferenciaReserva(params: any) {
    const response = await api.put('hospitais/motivo-transferencia-reserva', params)
    return factory.build(response)
  },

  async getMedicoByHisId(params: any) {
    const response = await api.get(`hospital/${params.hospitalId}/medicos/get-medico-by-hisid/${params.hisId}`)
    return factory.build(response)
  },

  async backofficeAgendamentoResolvePendenciaMedico(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/agendamento/pendencia-medico`, params)
    return factory.build(response)
  },

  async buscaCep(cep: string) {
    const request = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
      method: 'GET',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
    })
    const response = await request.json()
    return response
  },

  async criarHospitalUtilizandoModelo(params: any) {
    const response = await api.post('suporte/hospital/criar-utilizando-modelo', params)
    return factory.build(response)
  },

  async getAllCategoriasByPlano(params: any) {
    const response = await api.get(`hospitais/${params.hospitalId}/convenios/${params.convenioId}/plano/${params.planoId}/categorias`)
    return factory.build(response)
  },

  async backofficeAlterarMedico(params: any) {
    const response = await api.put('backoffice/alterar-medico', params)
    return factory.build(response)
  },

  async getBackofficeAssinaturaDoMedico(medicoId: string) {
    const response = await api.get(`backoffice/medico/${medicoId}/assinatura`)
    return factory.build(response)
  },

  async getMedicoDadosPreCadastro(medicoId: string) {
    const response = await api.get(`medico/${medicoId}/dados-cadastromedico`)
    return factory.build(response)
  },

  async getAllCadastrosMedicosByUsuarioHospital(params: any) {
    let query: string = ''

    const { pagina } = params
    const { qtdPorPagina } = params
    const { searchText } = params

    if (!['', null, undefined].includes(searchText)) query += `&searchText=${searchText}`
    query += `&pagina=${pagina || 1}&qtdPorPagina=${qtdPorPagina || 25}`
    query = `?${query.substring(1, query.length)}`

    const response = await api.get(`usuario-hospital/cadastrosmedicos${query}`)
    return factory.build(response)
  },

  async gerarPedidoSemAssinatura(params: any) {
    const response = await api.post(`solicitacoes/${params.solicitacaoId}/PedidoSemAssinatura`, params)
    return factory.build(response)
  },

  async consultaCoberturaVigenteBackoffice(params: any) {
    let query = `?convenioId=${params.convenioId}&planoId=${params.planoId}`

    if (params.categoriaId) query += `&categoriaId=${params.categoriaId}`

    if (params.procedimentoIds.length > 0) {
      params.procedimentoIds.forEach((procedimentoId: string) => {
        if (procedimentoId) query += `&procedimentoId=${procedimentoId}`
      })
    }

    const request = await api.get(`hospitais/${params.hospitalId}/cobertura-vigente${query}`)
    return factory.build(request)
  },

  async getGradeMapaCirurgico(params: any) {
    let query: string = ''
    const { hospitalId, data } = params
    if (hospitalId) query += `?hospitalId=${hospitalId}&data=${data || new Date().toISOString().substring(0, 10)}`
    const response = await api.get(`/grade-mapa-cirurgico${query}`)
    return factory.build(response)
  },

  async getPesquisaGradeMapaCirurgico(params: any) {
    const response = await api.get(`/pesquisa-grade-mapa-cirurgico?hospitalId=${params.hospitalId}&medicoSolicitanteId=${params.medicoSolicitanteId}`)
    return factory.build(response)
  },

  async getAllMedicosByHospitalId(params: any) {
    const response = await api.get(`/usuario-hospital/${params.usuarioId}/hospital/${params.hospitalId}/medicos`)
    return factory.build(response)
  },

  async getAllAgrupadorHospitaByUsuario(agrupadorTipo: string = '') {
    const response = await api.get(`/usuario-hospital/agrupador-hospital?agrupadorTipo=${agrupadorTipo}`)
    return factory.build<IAgrupadorHospital[]>(response)
  },

  async getAllProcedimentosTempoCirurgicoHospital(params: any) {
    let query: string = ''

    const { pagina } = params
    const { qtdPorPagina } = params
    const { searchText } = params

    if (!['', null, undefined].includes(searchText)) query += `&searchText=${searchText}`
    query += `&pagina=${pagina || 1}&qtdPorPagina=${qtdPorPagina || 25}`
    query = `?${query.substring(1, query.length)}`

    const response = await api.get(`backoffice/hospitais/${params.hospitalId}/procedimentos/tempo-cirurgico-hospital${query}`)
    return factory.build(response)
  },

  async getAllProcedimentosTempoCirurgicoMedico(params: any) {
    let query: string = ''

    const { pagina } = params
    const { qtdPorPagina } = params
    const { searchText } = params

    if (!['', null, undefined].includes(searchText)) query += `&searchText=${searchText}`
    query += `&pagina=${pagina || 1}&qtdPorPagina=${qtdPorPagina || 25}`
    query = `?${query.substring(1, query.length)}`

    const response = await api.get(`backoffice/hospitais/${params.hospitalId}/procedimentos/tempo-cirurgico-medico${query}`)
    return factory.build(response)
  },

  async upsertProcedimentosTempoCirurgico(params: any) {
    const response = await api.put(`backoffice/hospitais/${params.hospitalId}/procedimentos/tempo-cirurgico`, params)
    return factory.build(response)
  },

  async cargaTempoCirurgicoParametrizado(params: any) {
    const response = await api.post(`cadastro/hospital/${params.hospitalId}/procedimentos/tempo-cirurgico-parametrizado`, params)
    return factory.build(response)
  },

  async cargaProcedimentosHospitalDia(params: any) {
    const response = await api.post(`cadastro/hospital/${params.hospitalId}/procedimentos-hospital-dia`, params)
    return factory.build(response)
  },

  async backofficeFinalizaSolicitacaoNaFarmacia(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/finaliza-solicitacao-farmacia`, params)
    return factory.build(response)
  },

  async imprimirEtiqueta(params: any) {
    const response = await api.get(`solicitacao/${params.solicitacaoId}/imprimir-etiqueta/${params.tipo}`)
    return factory.build(response)
  },

  async getUserTermsOfUse(queryParamsData) {
    const queryParams = new URLSearchParams(queryParamsData).toString()
    const response = await api.get(`usuario/termos-aceite?${queryParams}`)
    return factory.build(response)
  },

  async getDoctorSignature(doctorId: string) {
    const response = await api.get(`medicos/${doctorId}/assinatura`)
    return factory.build<{ assinaturaMedico: string }>(response)
  },

  async backofficeAtualizaTipoMedicoEOrdenacao(params: any) {
    const response = await api.put(`backoffice/hospitais/${params.hospitalId}/medicos/${params.medicoId}/tipoMedicoEOrdenacao/${params.tipomedicoeordenacao}`, params)
    return factory.build(response)
  },

  async getAllRegraReservaSala(params: any) {
    const {
      hospitalId,
      pagina,
      qtdPorPagina,
      searchText,
      salaId,
      centroCirurgicoId,
      horaInicio,
      horaFim,
      tipo,
      frequencia,
      vinculoComMedicos,
      vinculoComEspecialidades,
      vinculoComConvenios,
    } = params

    let query: string = ''
    if (hospitalId) query += `&hospitalId=${params.hospitalId}`
    if (searchText) query += `&searchText=${searchText}`
    if (salaId) query += `&salaId=${salaId}`
    if (centroCirurgicoId) query += `&centroCirurgicoId=${centroCirurgicoId}`
    if (horaInicio) query += `&horaInicio=${horaInicio}`
    if (horaFim) query += `&horaFim=${horaFim}`
    if (tipo) query += `&tipo=${tipo}`
    if (frequencia) query += `&frequencia=${frequencia}`
    if (vinculoComMedicos) query += `&vinculoComMedicos=${vinculoComMedicos}`
    if (vinculoComEspecialidades) query += `&vinculoComEspecialidades=${vinculoComEspecialidades}`
    if (vinculoComConvenios) query += `&vinculoComConvenios=${vinculoComConvenios}`
    query += `&pagina=${pagina || 1}&qtdPorPagina=${qtdPorPagina || 25}`
    query = `?${query.substring(1, query.length)}`
    const response = await api.get(`regra-reserva-sala${query}`)
    return factory.build(response)
  },

  async getRegraReservaSalaById({ hospitalId, regraReservaSalaId }: { hospitalId: string, regraReservaSalaId: string }) {
    const response = await api.get(`hospital/${hospitalId}/regra-reserva-sala/${regraReservaSalaId}`)
    return factory.build(response)
  },

  async upsertRegraReserva(params: any) {
    const response = await api.put('/regra-reserva-sala', params)
    return factory.build<RegraReservaSalaUpsertResponse>(response)
  },

  async getTemplateMapaCirurgico() {
    const response = await api.get('/mapa-cirurgico/template')
    return factory.build(response)
  },

  async verifyPatientEligibility(surgerySolicitationId: string) {
    const response = await api.put(`/backoffice/solicitacao/${surgerySolicitationId}/verificar-elegibilidade-manual`, {})
    return factory.build(response)
  },

  async getAllInformacoesParaGerarAGuiaTiss({ solicitacaoId, tipoDoTemplate }: { solicitacaoId: string, tipoDoTemplate: TipoDocumentoUpload }) {
    const response = await api.get(`solicitacaoes/${solicitacaoId}/dados-template/${tipoDoTemplate}`)
    return factory.build(response)
  },

  async gerarResumo(solicitacaoId: string) {
    const response = await api.post(`solicitacoes/${solicitacaoId}/resumo`, {})
    return factory.build(response)
  },


  async backofficeAtualizaOrcamentoParticular(solicitacaoId: string) {
    const response = await api.put(`solicitacoes-backoffice/${solicitacaoId}/orcamento-particular`, { solicitacaoId })
    return factory.build(response)
  },

  async backofficeCancelaOrcamentoParticular(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/cancela-orcamento-particular`, params)
    return factory.build(response)
  },

  async backofficeOrcamentoPagamento(params: any) {
    const response = await api.put(`solicitacoes-backoffice/${params.solicitacaoId}/orcamento-pagamento`, params)
    return factory.build(response)
  },

  async backofficeFazPriorizacao(solicitacaoId: string, params: any) {
    const response = await api.put(`solicitacoes-backoffice/${solicitacaoId}/agendamento/faz-priorizacao`, params)
    return factory.build(response)
  },

  async backofficeAtualizarOrcamento(params: any) {
    const response = await api.put(`/solicitacao/${params.solicitacaoId}/atualiza-orcamento`, params)
    return factory.build(response)
  },
}
